import { useCallback, useMemo, useState } from 'react';
import { Upload, Button, Image, Space, message } from 'antd';
import { DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload';
import { useWisecureClient } from '@wisecure/api-client/hooks';

interface ImageUploadProps {
  value?: string;
  onChange?: (url: string | undefined) => void;
  folder?: string;
  disabled?: boolean;
}

export function ImageUpload({
  value,
  onChange,
  folder = 'feed',
  disabled = false,
}: ImageUploadProps) {
  const client = useWisecureClient();
  const [uploading, setUploading] = useState(false);

  const fileList: UploadFile[] = useMemo(() => {
    if (!value) return [];
    return [
      {
        uid: '-1',
        name: 'image',
        status: 'done',
        url: value,
      },
    ];
  }, [value]);

  const handleUpload = useCallback(
    async (file: RcFile) => {
      setUploading(true);
      try {
        const response = await client.uploads.uploadControllerUploadImage({
          formData: { file },
          folder,
          isPublic: true,
        });
        onChange?.(response.url);
        message.success('Image uploadée');
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Échec de l'upload";
        message.error(msg);
      } finally {
        setUploading(false);
      }
      return false;
    },
    [client, folder, onChange],
  );

  const handleRemove = useCallback(() => {
    onChange?.(undefined);
  }, [onChange]);

  return (
    <div>
      {value ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Image
            src={value}
            alt="Aperçu"
            style={{ maxHeight: 160, borderRadius: 8, objectFit: 'cover' }}
            preview
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg=="
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={handleRemove}
            disabled={disabled}
          >
            Supprimer l'image
          </Button>
        </Space>
      ) : (
        <Upload
          fileList={fileList}
          beforeUpload={handleUpload}
          onRemove={handleRemove}
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          maxCount={1}
          showUploadList={false}
          disabled={disabled || uploading}
        >
          <Button icon={<PictureOutlined />} loading={uploading} disabled={disabled}>
            {uploading ? 'Upload en cours...' : 'Choisir une image'}
          </Button>
        </Upload>
      )}
    </div>
  );
}
